/**
 * API 유틸리티 - 백엔드 URL 관리 및 HTTP 요청 통합
 */

import { config } from '../config';

// 기본 백엔드 URL (환경변수에서 가져옴)
const DEFAULT_API_BASE_URL = config.apiBaseUrl;
const API_BASE_URL_KEY = 'bus_admin_api_base_url';

/**
 * 저장된 백엔드 URL을 가져옵니다
 */
export const getApiBaseUrl = (): string => {
  const savedUrl = localStorage.getItem(API_BASE_URL_KEY);
  return savedUrl || DEFAULT_API_BASE_URL;
};

/**
 * 백엔드 URL을 저장합니다
 */
export const setApiBaseUrl = (url: string): void => {
  // URL 형식 검증
  try {
    const urlObj = new URL(url);
    if (!urlObj.protocol.startsWith('http')) {
      throw new Error('HTTP 또는 HTTPS 프로토콜을 사용해주세요');
    }
    localStorage.setItem(API_BASE_URL_KEY, url.replace(/\/$/, '')); // 마지막 슬래시 제거
  } catch (error) {
    throw new Error('올바른 URL 형식이 아닙니다');
  }
};

/**
 * 백엔드 URL을 기본값으로 재설정합니다
 */
export const resetApiBaseUrl = (): void => {
  localStorage.removeItem(API_BASE_URL_KEY);
};

/**
 * API 경로를 전체 URL로 변환합니다
 */
export const getApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * 업로드된 파일의 URL을 생성합니다
 */
export const getUploadsUrl = (filePath: string): string => {
  const baseUrl = getApiBaseUrl();
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
  return `${baseUrl}/uploads/${cleanPath}`;
};

/**
 * 백엔드 서버 연결 상태를 테스트합니다
 */
export const testApiConnection = async (url?: string): Promise<{ success: boolean; error?: string }> => {
  const testUrl = url || getApiBaseUrl();
  
  try {
    const response = await fetch(`${testUrl}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // 타임아웃 설정 (5초)
      signal: AbortSignal.timeout(5000)
    });
    
    if (response.ok) {
      return { success: true };
    } else {
      return { 
        success: false, 
        error: `서버 응답 오류: ${response.status} ${response.statusText}` 
      };
    }
  } catch (error: any) {
    let errorMessage = '알 수 없는 오류';
    
    if (error.name === 'TimeoutError') {
      errorMessage = '서버 응답 시간 초과 (5초)';
    } else if (error.message?.includes('Failed to fetch')) {
      errorMessage = '서버에 연결할 수 없습니다';
    } else {
      errorMessage = error.message || errorMessage;
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

/**
 * 인증된 API 요청을 위한 헤더를 생성합니다
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * 인증된 GET 요청
 */
export const apiGet = async (path: string): Promise<Response> => {
  return fetch(getApiUrl(path), {
    method: 'GET',
    headers: getAuthHeaders()
  });
};

/**
 * 인증된 POST 요청
 */
export const apiPost = async (path: string, data?: any): Promise<Response> => {
  return fetch(getApiUrl(path), {
    method: 'POST',
    headers: getAuthHeaders(),
    ...(data && { body: JSON.stringify(data) })
  });
};

/**
 * 인증된 PUT 요청
 */
export const apiPut = async (path: string, data?: any): Promise<Response> => {
  return fetch(getApiUrl(path), {
    method: 'PUT',
    headers: getAuthHeaders(),
    ...(data && { body: JSON.stringify(data) })
  });
};

/**
 * 인증된 DELETE 요청
 */
export const apiDelete = async (path: string): Promise<Response> => {
  return fetch(getApiUrl(path), {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
};

/**
 * 사용자 권한에 따라 URL에 회사 필터를 추가합니다
 */
export const addCompanyFilter = (url: string, companyId?: number | null): string => {
  if (companyId === null || companyId === undefined) {
    return url;
  }

  const urlObj = new URL(url, getApiBaseUrl());
  urlObj.searchParams.set('company_id', companyId.toString());
  return urlObj.toString();
};

/**
 * 권한이 있는 사용자 정보를 가져옵니다
 */
export const getCurrentUser = () => {
  const token = localStorage.getItem('adminToken');
  if (!token) return null;

  try {
    // JWT 토큰에서 사용자 정보 추출 (간단한 방법)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.id,
      username: payload.username,
      name: payload.name,
      role: payload.role,
      company_id: payload.company_id,
      company_name: payload.company_name
    };
  } catch (error) {
    console.warn('토큰 파싱 실패:', error);
    return null;
  }
};

/**
 * 운수사 스코프가 적용된 GET 요청
 */
export const apiGetWithScope = async (path: string, options?: {
  companyId?: number | null;
  additionalParams?: Record<string, string>;
}): Promise<Response> => {
  let url = getApiUrl(path);
  
  // 운수사 필터 추가
  if (options?.companyId !== undefined) {
    url = addCompanyFilter(url, options.companyId);
  }
  
  // 추가 파라미터
  if (options?.additionalParams) {
    const urlObj = new URL(url);
    Object.entries(options.additionalParams).forEach(([key, value]) => {
      urlObj.searchParams.set(key, value);
    });
    url = urlObj.toString();
  }

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
};

/**
 * 운수사 스코프가 적용된 POST 요청
 */
export const apiPostWithScope = async (path: string, data?: any, companyId?: number | null): Promise<Response> => {
  // POST 데이터에 회사 ID 추가
  const requestData = data ? { ...data } : {};
  if (companyId !== null && companyId !== undefined) {
    requestData.company_id = companyId;
  }

  return fetch(getApiUrl(path), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(requestData)
  });
};

/**
 * 운수사 스코프가 적용된 PUT 요청
 */
export const apiPutWithScope = async (path: string, data?: any, companyId?: number | null): Promise<Response> => {
  // PUT 데이터에 회사 ID 추가
  const requestData = data ? { ...data } : {};
  if (companyId !== null && companyId !== undefined) {
    requestData.company_id = companyId;
  }

  return fetch(getApiUrl(path), {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(requestData)
  });
};